import { TFunction } from 'i18next';
import { joinProjectNames } from 'components/Project/projectNames';
import { Iso27001Rooms } from 'models/projects/iso27001Rooms';
import type { ProjectRoomTodo } from 'models/projects/project';
import { capitalizeFirstLetter } from 'utils/string';

// The words of the next move: the action each room or step asks for, what is still open in a room, and what doing it
// gains. Pure (no React); translation keys are string literals so the scanner finds them.

/** Days per week for the gain. */
const projectNextMoveDaysPerWeek: number = 7;

/** Above this many days the gain is stated in weeks. */
const projectNextMoveWeekThreshold: number = 6;

/** The gain as an amount, "een dag", "2 dagen", "een week", "3 weken"; undefined for no gain or an unknown one. */
const getNextMoveAmount = (t: TFunction<string[]>, impactDays: number | undefined): string | undefined => {
  if (impactDays === undefined || impactDays <= 0) return undefined;

  if (impactDays > projectNextMoveWeekThreshold) {
    return t('projects:NextMove.Weeks', { count: Math.round(impactDays / projectNextMoveDaysPerWeek) });
  }

  return t('projects:NextMove.Days', { count: impactDays });
};

/**
 * The gain as a sentence: "Als dit klaar is, is uw project naar verwachting een week eerder klaar." Undefined when the
 * move gains nothing (0) or the forecast does not know yet.
 */
export const getNextMoveGain = (t: TFunction<string[]>, impactDays: number | undefined): string | undefined => {
  const amount = getNextMoveAmount(t, impactDays);

  return amount === undefined ? undefined : t('projects:NextMove.Gain', { amount: amount });
};

/** One fact of a room's to-do in words, "1 uitvraag is te laat"; undefined for a key this client does not know. */
const getTodoText = (t: TFunction<string[]>, fact: ProjectRoomTodo): string | undefined => {
  const values = { count: fact.count };

  switch (fact.key) {
    case 'inquiriesLate':
      return t('projects:NextMove.Todo.inquiriesLate', values);
    case 'rolesToChoose':
      return t('projects:NextMove.Todo.rolesToChoose', values);
    case 'inquiriesNotSent':
      return t('projects:NextMove.Todo.inquiriesNotSent', values);
    case 'inquiriesReadyToSend':
      return t('projects:NextMove.Todo.inquiriesReadyToSend', values);
    case 'rolesWithoutResponsible':
      return t('projects:NextMove.Todo.rolesWithoutResponsible', values);
    case 'rolesWithoutLicence':
      return t('projects:NextMove.Todo.rolesWithoutLicence', values);
    case 'documentsNotApproved':
      return t('projects:NextMove.Todo.documentsNotApproved', values);
    case 'documentsAwaitingApproval':
      return t('projects:NextMove.Todo.documentsAwaitingApproval', values);
    case 'assetsToAssign':
      return t('projects:NextMove.Todo.assetsToAssign', values);
    case 'assetsToGroup':
      return t('projects:NextMove.Todo.assetsToGroup', values);
    case 'assetDuplicatesOpen':
      return t('projects:NextMove.Todo.assetDuplicatesOpen', values);
    default:
      return undefined;
  }
};

/**
 * What is still open in a room as one sentence, in the server's order: "1 uitvraag is te laat en 1 bericht wacht op u."
 * Undefined when the room names nothing this client can word.
 */
export const getNextMoveTodoLine = (t: TFunction<string[]>, todo: ProjectRoomTodo[]): string | undefined => {
  const parts: string[] = todo
    .filter((fact) => fact.count > 0)
    .map((fact) => getTodoText(t, fact))
    .filter((text): text is string => text !== undefined && text.length > 0);
  if (parts.length === 0) return undefined;

  return t('projects:NextMove.TodoLine', { things: capitalizeFirstLetter(joinProjectNames(parts, t)) });
};

/** The action a room asks for: "Volg de uitvragen op"; for a room this client does not know, "Ga verder met {name}". */
export const getRoomMoveTitle = (t: TFunction<string[]>, roomKey: string, name: string): string => {
  switch (roomKey) {
    case Iso27001Rooms.Profile:
      return t('projects:NextMove.Title.profile');
    case Iso27001Rooms.Scope:
      return t('projects:NextMove.Title.scope');
    case Iso27001Rooms.Objectives:
      return t('projects:NextMove.Title.objectives');
    case Iso27001Rooms.Risks:
      return t('projects:NextMove.Title.risks');
    case Iso27001Rooms.Roles:
      return t('projects:NextMove.Title.roles');
    case Iso27001Rooms.Documents:
      return t('projects:NextMove.Title.documents');
    case Iso27001Rooms.AssetInquiry:
      return t('projects:NextMove.Title.assetInquiry');
    case Iso27001Rooms.OutcomeInquiry:
      return t('projects:NextMove.Title.outcomeInquiry');
    case Iso27001Rooms.InquiryProgress:
      return t('projects:NextMove.Title.inquiryProgress');
    case Iso27001Rooms.AssetRegister:
      return t('projects:NextMove.Title.assetRegister');
    case Iso27001Rooms.ExternalAudit:
      return t('projects:NextMove.Title.externalAudit');
    default:
      return t('projects:NextMove.Title.Other', { name: name });
  }
};

/** The step facts the words read: its seed key (undefined when authored by hand) and its name. */
interface IStepFacts {
  key?: string;

  name: string;
}

/**
 * The action a step asks for: a step of the package is named as an action already ("Beoordeel de risico's met hun
 * eigenaren"); a step authored by hand gets "Ga verder met {name}".
 */
export const getStepMoveTitle = (t: TFunction<string[]>, step: IStepFacts): string => {
  if (step.key !== undefined) return step.name;

  return t('projects:NextMove.Title.Other', { name: step.name });
};

/** The button of a step: a step is named as an order, so the button says Naar de stap. */
export const getStepMoveButton = (t: TFunction<string[]>, step: IStepFacts): string => {
  return t('projects:NextMove.GoToStep');
};
